<script setup>

// Imports
import { ref } from 'vue'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

// State: id/key of expanded item (all collapsed by default)
const expandedId = ref(null)

// Helpers
const getKey = (item, index) => (item?.id ?? index)

// Methods
const toggleItem = (item, index) => {
  const key = getKey(item, index)
  expandedId.value = expandedId.value === key ? null : key
}

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section
  :id="block?.['anchorId']"
  data-hydration="lazy-hydration"
  class="faq --delight section-mb"
  role="region"
  aria-label="Frequently Asked Questions"
>
  <div class="container faq__container">
    <div class="faq__heading">
      <div
        v-if="block.heading?.html?.length"
        v-html="block.heading?.html"
        class="faq__title"
      />
      <p v-if="block.description" class="text-b1">
        {{ block.description }}
      </p>
    </div>

    <div
      v-if="(block.faqContent[0] || [])?.items?.length"
      class="faq__items"
      role="list"
    >
      <div
        v-for="(item, idx) in (block.faqContent[0] || [])?.items"
        :key="item.id || idx"
        class="faq__item"
        :class="{ 'is-active': expandedId === getKey(item, idx) }"
        role="listitem"
      >
        <button
          type="button"
          class="faq__question"
          @click="toggleItem(item, idx)"
          :aria-expanded="expandedId === getKey(item, idx) ? 'true' : 'false'"
          :aria-controls="`faq-answer-${getKey(item, idx)}`"
          :id="`faq-question-${getKey(item, idx)}`"
        >
          <span>{{ item.question }}</span>
          <span class="faq__question-icon" aria-hidden="true"><span /></span>
        </button>

        <div
          class="faq__answer-wrapper"
          :class="{ 'is-open': expandedId === getKey(item, idx) }"
          :id="`faq-answer-${getKey(item, idx)}`"
          role="region"
          :aria-labelledby="`faq-question-${getKey(item, idx)}`"
          :aria-hidden="expandedId === getKey(item, idx) ? undefined : 'true'"
          :inert="expandedId !== getKey(item, idx)"
        >
          <div class="faq__answer" v-html="item.answer?.html" />
        </div>
      </div>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.faq {
  content-visibility: auto;
  contain-intrinsic-size: 530px;

  @media (min-width: 1200px) {
    contain-intrinsic-size: 350px;
  }
}

.faq__heading {
  margin-bottom: 32px;
  text-align: center;
}

.faq__title {
  margin-bottom: 16px;
}

.faq__item {
  border-top: 1px solid var(--color-gray-200);
  padding-top: 24px;
  padding-bottom: 24px;
  transition: all 0.3s ease;
}

.faq__item.is-active {
  border-top: 2px solid var(--color-gray-900);
  padding-top: 16px;
}

.faq__item:not(.is-active):hover {
  background-color: var(--color-gray-100);
}

.faq__item:last-child {
  border-bottom: 1px solid var(--color-gray-200);
}

.faq__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 20px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.4px;
  color: var(--color-gray-700);
  background: none;
  border: none;
  padding: 0;
  width: 100%;
  text-align: left;
}

.faq__question-icon {
  position: relative;
  margin-left: 8px;
  border-radius: 50%;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.faq__question-icon:before,
.faq__question-icon:after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 2px;
  background-color: var(--color-black-100);
}

.faq__question-icon:after {
  transform: translate(-50%, -50%) rotate(90deg);
  transition: transform 0.3s ease;
  transform-origin: center;
}

.faq__item.is-active .faq__question-icon:after {
  transform: translate(-50%, -50%) rotate(0deg);
  opacity: 0;
}

/* FAQ Answer transition */
.faq__answer-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq__answer-wrapper.is-open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.faq__answer {
  min-height: 0;
  line-height: 140%;
  letter-spacing: -0.5px;
  color: var(--color-gray-400);
  margin-top: 0;
  transition: margin-top 0.3s ease;

  :deep(a) {
    color: #000;
    font-weight: 500;
  }
}

.faq__answer-wrapper.is-open .faq__answer {
  margin-top: 16px;
}

@media (min-width: 1024px) {
  .faq__container {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  .faq__heading {
    margin-bottom: 0;
    text-align: left;
    width: calc(572 / 1552 * 100%);
    flex-shrink: 0;
  }

  .faq__title {
    margin-bottom: 24px;
  }

  .faq__question {
    font-size: 22px;
    letter-spacing: -0.44px;
  }

  .faq__question-icon {
    height: 38px;
    width: 38px;
  }

  .faq__answer {
    font-size: 18px;
    letter-spacing: -0.36px;
  }
}

@media (min-width: 1024px) {
  .faq :deep(.faq__items) {
    flex-grow: 1;
    max-width: 859px;
  }
}

</style>

